import 'src/locales/i18n';

import { LicenseInfo } from '@mui/x-license';

import ProgressBar from 'src/components/progress-bar';
import { LocalizationProvider } from 'src/locales';
import { shouldRetryQuery } from 'src/utils/query-retry';
import { AMPLITUDE_API_KEY, QUERY_DEVTOOLS_DISABLED } from './config-global';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { LazyMotion } from 'framer-motion';
import { lazy, useEffect, useMemo, useState } from 'react';

import 'src/global.css';

import { AppUpdateChecker } from './components/app-update';
import { CapacitorSetup } from './components/capacitor-utils';
// Imported eagerly on purpose: as a lazy provider above the whole tree it
// gated the first paint on its analytics chunk. The module is tiny — the SDK
// itself is loaded after mount from inside the provider.
import AmplitudeProvider from './components/data-analyser/AmplitudeContext';
import { PersistentMessagesProvider } from './components/persistent-messages';
import { RouterWithScrollToTop } from './components/router-with-scroll-to-top';
import { scheduleAssetRecoveryClear } from './utils/asset-recovery';

// Loaded after first paint: `LazyMotion` exists precisely so the animation
// engine stays out of the entry chunk, which a static `features` import undoes.
const loadMotionFeatures = () =>
  import('./components/animate/motion-features').then((mod) => mod.default);

const Router = lazy(() => import('src/routes/sections/router'));
const AuthProvider = lazy(() => import('src/auth/context/jwt'));
const ThemeProvider = lazy(() => import('src/theme'));
const SnackbarProvider = lazy(
  () => import('src/components/snackbar/snackbar-provider'),
);

//AMPLITUDE KEY
const amplitudeApiKey = AMPLITUDE_API_KEY;

// MUI Pro
LicenseInfo.setLicenseKey(
  '63cdcff003c86a961f1b47b5703dd5e0Tz0wLEU9MjUzNDA0ODY0MDAwMDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sS1Y9Mg==',
);

// ----------------------------------------------------------------------

export default function App() {
  // Keep the recovery marker briefly so initial lazy route chunks can settle.
  useEffect(() => {
    return scheduleAssetRecoveryClear();
  }, []);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 20 * 1000, // 20 seconds
            gcTime: 30 * 60 * 1000, // 30 minutes
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,
            networkMode: 'online', // Attend le réseau pour agir
            retry: shouldRetryQuery,
          },
          mutations: {
            retry: false,
          },
        },
      }),
  );

  return useMemo(
    () => (
      <>
        <QueryClientProvider client={queryClient}>
          <AmplitudeProvider apiKey={amplitudeApiKey}>
            <AuthProvider>
              <LocalizationProvider>
                <ThemeProvider>
                  <LazyMotion strict features={loadMotionFeatures}>
                    <SnackbarProvider>
                      <PersistentMessagesProvider>
                        {/* Mounted above the router: a detected update is queued
                            in provider state and surfaces once the dashboard
                            layout (which renders PersistentMessages) is shown. */}
                        <AppUpdateChecker />
                        <CapacitorSetup />
                        <ProgressBar />
                        <RouterWithScrollToTop>
                          <Router />
                        </RouterWithScrollToTop>
                      </PersistentMessagesProvider>
                    </SnackbarProvider>
                  </LazyMotion>
                </ThemeProvider>
              </LocalizationProvider>
            </AuthProvider>
          </AmplitudeProvider>
          {!QUERY_DEVTOOLS_DISABLED && (
            <ReactQueryDevtools
              initialIsOpen={false}
              buttonPosition="top-left"
            />
          )}
        </QueryClientProvider>
      </>
    ),
    [queryClient],
  );
}
